import { jsPDF } from 'jspdf';

export const generateInvoicePDF = async (invoiceData) => {
  const { practice, entries, total, invoiceNumber, profile, author, subject } = invoiceData;
  
  try {
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('InvoicePDFGenerator received:', invoiceData);
      console.log('PDF Generator received entries:', JSON.stringify(entries, null, 2));
      
      // Log each patient entry in detail for debugging
      entries.forEach((entry, i) => {
        if (entry.patients && entry.patients.length > 0) {
          console.log(`Entry ${i} patients with descriptions:`, entry.patients.map(p => ({
            count: p.count,
            rate: p.rate,
            description: p.description,
            subtotal: p.subtotal,
            total: p.total
          })));
        }
      });
    }
    
    const doc = new jsPDF();
    
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const rightMargin = pageWidth - margin;
    const purple = [128, 0, 128];

    // Helper functions
    const safeText = (text, x, y, options = {}) => {
      if (text != null && text !== 'undefined' && text !== 'NA') {
        doc.text(String(text), x, y, options);
      }
    };

    // Title - largest size
    doc.setFontSize(20);
    doc.setTextColor(...purple);
    safeText('INVOICE', pageWidth / 2, 20, { align: 'center' });

    // Reset text color for rest of document
    doc.setTextColor(0, 0, 0);

    // Addresses and main content - medium size
    doc.setFontSize(11);
    let yPos = 40;
    const lineSpacing = 7;

    // --- From section (Left side) ---
    let currentY = yPos;
    if (profile) {
      safeText(profile.name, margin, currentY);
      
      if (profile.address1 && profile.address1 !== 'NA') {
        currentY += lineSpacing;
        safeText(profile.address1, margin, currentY);
      }
      if (profile.address2 && profile.address2 !== 'NA') {
        currentY += lineSpacing;
        safeText(profile.address2, margin, currentY);
      }
      if (profile.city && profile.city !== 'NA') {
        currentY += lineSpacing;
        safeText(profile.city, margin, currentY);
      }
      if (profile.postcode && profile.postcode !== 'NA') {
        currentY += lineSpacing;
        safeText(profile.postcode, margin, currentY);
      }
    }
    let leftAddressEndY = currentY;

    // --- To section (Right side) ---
    currentY = yPos;
    if (practice) {
      safeText(practice.prac_name, rightMargin, currentY, { align: 'right' });
      
      if (practice.address1 && practice.address1 !== 'NA') {
        currentY += lineSpacing;
        safeText(practice.address1, rightMargin, currentY, { align: 'right' });
      }
      if (practice.address2 && practice.address2 !== 'NA') {
        currentY += lineSpacing;
        safeText(practice.address2, rightMargin, currentY, { align: 'right' });
      }
      if (practice.address3 && practice.address3 !== 'NA') {
        currentY += lineSpacing;
        safeText(practice.address3, rightMargin, currentY, { align: 'right' });
      }
      if (practice.address4 && practice.address4 !== 'NA') {
        currentY += lineSpacing;
        safeText(practice.address4, rightMargin, currentY, { align: 'right' });
      }
      if (practice.postcode && practice.postcode !== 'NA') {
        currentY += lineSpacing;
        safeText(practice.postcode, rightMargin, currentY, { align: 'right' });
      }
    }
    let rightAddressEndY = currentY;

    // --- Invoice details ---
    yPos = Math.max(leftAddressEndY, rightAddressEndY) + 20;
    safeText(`Invoice Number: ${invoiceNumber}`, margin, yPos);
    safeText(`Date: ${new Date().toLocaleDateString()}`, rightMargin, yPos, { align: 'right' });

    // Table data preparation
    const tableData = [];

    entries.forEach(entry => {
      if (!entry) return;

      const dateStr = entry.formattedDate || (entry.date ? new Date(entry.date).toLocaleDateString() : '');
      const description = entry.description || '';
      const amount = entry.amount || 0;

      tableData.push([
        dateStr,
        description,
        '',
        `£${parseFloat(amount).toFixed(2)}`
      ]);
    });

    // Generate table using reliable manual generation
    yPos += 20;
    console.log('Generating table using manual method. Current yPos:', yPos);
    yPos = generateManualTable(doc, tableData, yPos, margin, rightMargin);

    // Continue with total and bank details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');

    const totalValue = typeof total === 'string' ? parseFloat(total) : (total || 0);
    safeText(`Total: £${totalValue.toFixed(2)}`, rightMargin, yPos, { align: 'right' });

    // Bank details and payment terms
    if (profile) {
      yPos += 20;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      safeText('Bank Details:', margin, yPos);
      safeText(`Bank: ${profile.bankName}`, margin, yPos + 7);
      safeText(`Sort Code: ${profile.sortCode}`, margin, yPos + 14);
      safeText(`Account Number: ${profile.accountNumber}`, margin, yPos + 21);

      yPos += 35;
      safeText('Payment is due within 30 days from date of invoice', margin, yPos);
    }

    const filename = `Invoice_${invoiceNumber}_${Date.now()}.pdf`;
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('PDF generated successfully with filename:', filename);
      console.log('PDF file saved successfully');
    }
    
    doc.save(filename);
    return { doc, filename };
  } catch (error) {
    console.error('Error in generateInvoicePDF:', error.message);
    throw error;
  }
};

// Enhanced manual table generation with better styling
function generateManualTable(doc, tableData, startY, margin, rightMargin) {
  const pageWidth = doc.internal.pageSize.width;
  const tableWidth = pageWidth - (margin * 2);
  
  // Column widths - improved distribution
  const dateWidth = 35;
  const amountWidth = 35;
  const descriptionWidth = tableWidth - dateWidth - amountWidth;
  
  let currentY = startY;
  
  // Table header with better styling
  doc.setFillColor(128, 0, 128);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  
  // Header background with border
  doc.rect(margin, currentY, tableWidth, 10, 'FD');
  
  // Header text with better positioning
  doc.text('Date', margin + 3, currentY + 6.5);
  doc.text('Description', margin + dateWidth + 3, currentY + 6.5);
  doc.text('Amount', rightMargin - 3, currentY + 6.5, { align: 'right' });
  
  currentY += 10;
  
  // Table rows with improved styling
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  
  tableData.forEach((row, index) => {
    // Alternate row colors for better readability
    if (index % 2 === 0) {
      doc.setFillColor(248, 249, 250);
      doc.rect(margin, currentY, tableWidth, 8, 'F');
    }
    
    // Add subtle borders
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.1);
    doc.line(margin, currentY, margin + tableWidth, currentY);
    
    // Row text with improved spacing
    doc.text(row[0] || '', margin + 3, currentY + 5.5); // Date
    
    // Handle long descriptions by wrapping text
    const description = row[1] || '';
    if (description.length > 40) {
      const lines = doc.splitTextToSize(description, descriptionWidth - 6);
      doc.text(lines[0], margin + dateWidth + 3, currentY + 5.5);
      if (lines.length > 1) {
        // Add ellipsis if text is too long
        doc.text(lines[0].substring(0, lines[0].length - 3) + '...', margin + dateWidth + 3, currentY + 5.5);
      }
    } else {
      doc.text(description, margin + dateWidth + 3, currentY + 5.5);
    }
    
    doc.text(row[3] || '', rightMargin - 3, currentY + 5.5, { align: 'right' }); // Amount
    
    currentY += 8;
  });
  
  // Bottom border for table
  doc.setDrawColor(128, 0, 128);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, margin + tableWidth, currentY);
  
  return currentY + 15;
}